import { ReportStatus } from '@marmon/shared/types/report';

/**
 * Firestore config collection document IDs.
 * Add new document IDs here as config documents are created.
 */
export const ConfigDocumentIds = {
	Insights: 'insights',
	Reports: 'reports',
	Support: 'support',
	Time: 'time',
} as const;

export type ConfigDocumentId =
	(typeof ConfigDocumentIds)[keyof typeof ConfigDocumentIds];

/**
 * Per-report-type configuration. Keys in ReportsConfig match report type IDs
 * (e.g. eighty-twenty, qbr). Metadata is for report-specific settings.
 * allowedDownloadStatuses: report statuses for which PDF download is allowed
 * (e.g. 80/20 allows all; QBR allows only non-draft statuses).
 */
export interface ReportTypeConfig {
	name: string;
	enabled: boolean;
	allowedDownloadStatuses: ReportStatus[];
	metadata: Record<string, unknown>;
}

/**
 * Reports config document. Stored at config/reports.
 * Map of report type ID to its configuration.
 */
export interface ReportsConfig {
	[key: string]: ReportTypeConfig;
}

/**
 * Support config document. Stored at config/support.
 */
export interface SupportConfig {
	email: string;
}

/**
 * Month and day only (year-agnostic). month is 1–12, day is 1–31.
 * Used to define quarter start dates in the time config.
 */
export interface MonthDate {
	month: number;
	day: number;
}

/**
 * Time config document. Stored at config/time.
 * quarters: [Q1 start, Q2 start, Q3 start, Q4 start]. Each quarter’s end
 * is the day before the next quarter’s start; Q4’s end is the day before
 * the next year’s Q1 start so every date falls in exactly one quarter.
 */
export interface TimeConfig {
	quarters: [MonthDate, MonthDate, MonthDate, MonthDate];
}

/**
 * System-managed insight settings. Stored at config/insights.
 * `enabled` is the feature switch. The duration stats are updated by the
 * insights service as generations finish and drive the poll interval.
 */
export interface InsightsConfig {
	enabled: boolean;
	requestDurationAvgMillis: number;
	requestCount: number;
}

export const DefaultInsightsConfig: InsightsConfig = {
	enabled: false,
	requestDurationAvgMillis: 0,
	requestCount: 0,
};

/**
 * Map of config document IDs to their document types.
 * Enables type-safe fetching of config documents.
 */
export interface ConfigDocumentMap {
	[ConfigDocumentIds.Insights]: InsightsConfig;
	[ConfigDocumentIds.Reports]: ReportsConfig;
	[ConfigDocumentIds.Support]: SupportConfig;
	[ConfigDocumentIds.Time]: TimeConfig;
}
